// =============================================
// linkResolver — pre-publish strip + post-publish heal dla linków wewnętrznych
// =============================================
//
// Problem: pipeline LLM generuje twardy `<a href="/slug">anchor</a>` w final_html
// na podstawie thematic_nodes.slug. Przy publikacji partiami (~10/dzień), wpisy
// opublikowane wcześniej linkują do nodów które jeszcze NIE są live → 404.
//
// Rozwiązanie (Opcja B, akceptowane 2026-06-09):
//
// 1. PRE-PUBLISH (stripPendingLinks): przed wysłaniem na WP, normalizujemy
//    KAŻDY relative link wewnętrzny `<a href="/slug">anchor</a>`:
//    - cel ma wp_post_url (live) → <a href="${wp_post_url}">anchor</a>
//      (kanoniczny absolutny URL — naprawia rozjazd slugify ł→l w polskich slugach)
//    - cel bez wp_post_url (pending) → <span data-pending-link="/slug">anchor</span>
//      (inert marker, anchor text zachowany dla czytelności)
//
// 2. POST-PUBLISH (healPendingLinks): po opublikowaniu noda X, scan markerów
//    `data-pending-link="/X"` w existing wpisach → replace na <a href="${X.wp_post_url}">.
//    Idempotent — matchuje TYLKO marker, nigdy generic <a>.
//
// Edge cases obsłużone:
// - Polskie znaki w slugu (ł, ą, ę) — match przez [^"\s/?#]+
// - Anchor z nested elements (<em>, <strong>) — match przez [\s\S]*?
// - Self-link guard: skip linki noda do siebie
// - Absolute links (https://...) — nieruszane (mogą być WP existing lub external)

// Match relative href="/slug" lub "/slug/" lub "/slug?query" lub "/slug#hash".
// Slug = wszystko prócz quote/whitespace/path-separator/query-fragment.
// Anchor = lazy match żeby obsłużyć nested elements (<em>, etc.).
const RELATIVE_LINK_RE =
  /<a\s+href="\/([^"\s/?#]+)\/?(?:[?#][^"]*)?"\s*[^>]*>([\s\S]*?)<\/a>/gi;

// Match marker pending — exact format wygenerowany przez stripPendingLinks.
// Idempotency: heal patrzy TYLKO na ten marker, NIGDY na zwykły <a>.
const PENDING_MARKER_RE =
  /<span\s+data-pending-link="\/([^"\s/?#]+)"\s*[^>]*>([\s\S]*?)<\/span>/gi;

export interface StripResult {
  html: string;
  /** Linki ZAMIENIONE na marker (pending). */
  pendingMarkers: Array<{ slug: string; anchor: string }>;
  /** Linki ZNORMALIZOWANE na absolutny URL (live target). */
  normalizedToAbsolute: Array<{ slug: string; wpUrl: string; anchor: string }>;
}

export interface HealResult {
  html: string;
  healed: Array<{ slug: string; wpUrl: string; anchor: string }>;
}

/**
 * Pre-publish normalize: każdy relative <a href="/slug"> w HTML jest sprawdzany w mapie targetów.
 *
 * @param html — final_html do normalizacji (zwykle zawiera 5-10 relative linków)
 * @param slugToWpUrl — mapa slug → wp_post_url (null = pending, string = live)
 * @param selfSlug — slug noda PISZĄCEGO (skip self-link, zwykle nigdy nie ma)
 * @returns { html: znormalizowany, pendingMarkers: [...], normalizedToAbsolute: [...] }
 *
 * Linki nieobecne w mapie (orphan, external, manual) — NIETKNIĘTE.
 */
export function stripPendingLinks(
  html: string,
  slugToWpUrl: Map<string, string | null>,
  selfSlug?: string | null,
): StripResult {
  if (!html) {
    return { html: "", pendingMarkers: [], normalizedToAbsolute: [] };
  }

  const pendingMarkers: Array<{ slug: string; anchor: string }> = [];
  const normalizedToAbsolute: Array<{
    slug: string;
    wpUrl: string;
    anchor: string;
  }> = [];

  const newHtml = html.replace(
    RELATIVE_LINK_RE,
    (match, rawSlug: string, anchor: string) => {
      const slug = rawSlug.trim();
      const safeAnchor = anchor; // zachowujemy 1:1 (HTML entities, nested tags)

      // Self-link guard
      if (selfSlug && slug === selfSlug) {
        return match;
      }

      // Orphan link (nie w mapie nodów tej mapy) — zostaw bez zmian (może to manual link)
      if (!slugToWpUrl.has(slug)) {
        return match;
      }

      const wpUrl = slugToWpUrl.get(slug);

      if (wpUrl) {
        // Live target — wstaw kanoniczny absolutny URL
        normalizedToAbsolute.push({ slug, wpUrl, anchor: safeAnchor });
        return `<a href="${wpUrl}">${safeAnchor}</a>`;
      } else {
        // Pending target — wstaw marker (anchor text widoczny)
        pendingMarkers.push({ slug, anchor: safeAnchor });
        return `<span data-pending-link="/${slug}">${safeAnchor}</span>`;
      }
    },
  );

  return { html: newHtml, pendingMarkers, normalizedToAbsolute };
}

/**
 * Post-publish heal: każdy marker <span data-pending-link="/slug"> jest sprawdzany
 * w mapie resolutions. Jeśli slug jest w mapie (target właśnie wszedł live) →
 * replace na <a href="${wp_post_url}">anchor</a>. Pozostałe markery (target nadal
 * niepublikowany) zostają.
 *
 * Idempotent: matchuje TYLKO marker, nigdy generic <a>. Re-run nie zmienia
 * juz-zhealowanych linków.
 *
 * @param html — final_html z markerami
 * @param resolutions — slug → wp_post_url (tylko dla slugów do healowania)
 * @returns { html: zhealowany, healed: [...] }
 */
export function healPendingLinks(
  html: string,
  resolutions: Map<string, string>,
): HealResult {
  if (!html) {
    return { html: "", healed: [] };
  }
  if (resolutions.size === 0) {
    return { html, healed: [] };
  }

  const healed: Array<{ slug: string; wpUrl: string; anchor: string }> = [];

  const newHtml = html.replace(
    PENDING_MARKER_RE,
    (match, rawSlug: string, anchor: string) => {
      const slug = rawSlug.trim();
      const wpUrl = resolutions.get(slug);

      if (!wpUrl) {
        // Slug nie w mapie resolutions → zostaw marker (target nadal pending)
        return match;
      }

      healed.push({ slug, wpUrl, anchor });
      return `<a href="${wpUrl}">${anchor}</a>`;
    },
  );

  return { html: newHtml, healed };
}

/**
 * Helper: wyciąga listę slugów z aktywnych markerów <span data-pending-link>.
 * Używane przez heal job orchestrator do query "kto ma marker do /X".
 */
export function extractPendingSlugs(html: string): string[] {
  if (!html) return [];
  const slugs = new Set<string>();
  const matches = html.matchAll(PENDING_MARKER_RE);
  for (const m of matches) {
    const slug = m[1]?.trim();
    if (slug) slugs.add(slug);
  }
  return Array.from(slugs);
}
